import { useState, useEffect, useCallback } from 'react';
import { getHolidays, getHolidayScopesForCCAA } from '@/services/holidayService';
import { calculateDueDate, addBusinessDays, getNextBusinessDay } from '@/services/dateCalculationService';
import { useAuth } from '@/hooks/useAuth';
import type { Holiday, HolidayScope, DeadlineDayType } from '@/types/deadlines';

export function useHolidays() {
  const { profile } = useAuth();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const autonomousCommunity = (profile as any)?.autonomous_community || 'otra';
  const organizationId = (profile as any)?.organization_id || undefined;

  // Obtener los ámbitos de festivos aplicables
  const scopes: HolidayScope[] = getHolidayScopesForCCAA(autonomousCommunity);

  // Cargar festivos para el año actual y siguiente
  const loadHolidays = useCallback(async () => {
    setIsLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      
      const [currentYearHolidays, nextYearHolidays] = await Promise.all([
        getHolidays(currentYear, scopes, organizationId),
        getHolidays(nextYear, scopes, organizationId),
      ]);
      
      setHolidays([...currentYearHolidays, ...nextYearHolidays]);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setIsLoading(false);
    }
  }, [autonomousCommunity, organizationId]);

  // Cargar festivos al montar
  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Calcular fecha límite considerando festivos
  const calculateDeadlineDate = useCallback(async (
    startDate: Date,
    days: number,
    dayType: DeadlineDayType
  ): Promise<{ dueDate: Date; originalDueDate: Date; wasAdjusted: boolean }> => {
    const result = await calculateDueDate(
      startDate,
      days,
      dayType,
      autonomousCommunity,
      organizationId
    );
    
    const wasAdjusted = result.dueDate.getTime() !== result.originalDueDate.getTime();
    
    return {
      dueDate: result.dueDate,
      originalDueDate: result.originalDueDate,
      wasAdjusted,
    };
  }, [autonomousCommunity, organizationId]);

  // Verificar si una fecha es festivo
  const isHoliday = useCallback((date: Date): Holiday | null => {
    const dateStr = date.toISOString().split('T')[0];
    return holidays.find(h => h.date === dateStr) || null;
  }, [holidays]);

  // Verificar si una fecha es día hábil
  const isBusinessDay = useCallback((date: Date): boolean => {
    const day = date.getDay();
    if (day === 0 || day === 6) return false; // Fin de semana
    return !isHoliday(date);
  }, [isHoliday]);

  // Obtener siguiente día hábil
  const getNextWorkingDay = useCallback((date: Date): Date => {
    return getNextBusinessDay(date, holidays);
  }, [holidays]);

  // Añadir días hábiles
  const addWorkingDays = useCallback((startDate: Date, days: number): Date => {
    return addBusinessDays(startDate, days, holidays);
  }, [holidays]);

  // Obtener festivos del mes
  const getHolidaysForMonth = useCallback((year: number, month: number): Holiday[] => {
    return holidays.filter(h => {
      const date = new Date(h.date);
      return date.getFullYear() === year && date.getMonth() === month;
    });
  }, [holidays]);

  return {
    holidays,
    scopes,
    autonomousCommunity,
    isLoading,
    loadHolidays,
    calculateDeadlineDate,
    isHoliday,
    isBusinessDay,
    getNextWorkingDay,
    addWorkingDays,
    getHolidaysForMonth,
  };
}
